/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import { GetSharedSkillRequest } from "../models/operations/getsharedskill.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildSharedSkillQuery,
  prefetchSharedSkill,
  queryKeySharedSkill,
  SharedSkillQueryData,
} from "./sharedSkill.core.js";
export {
  buildSharedSkillQuery,
  prefetchSharedSkill,
  queryKeySharedSkill,
  type SharedSkillQueryData,
};

export type SharedSkillQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * getShared skills
 *
 * @remarks
 * Fetch the publicly shared view of a skill by its share token. This endpoint is unauthenticated and only ever exposes the skill name, display name, summary, and latest content.
 */
export function useSharedSkill(
  request: GetSharedSkillRequest,
  options?: QueryHookOptions<SharedSkillQueryData, SharedSkillQueryError>,
): UseQueryResult<SharedSkillQueryData, SharedSkillQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildSharedSkillQuery(
      client,
      request,
      options,
    ),
    ...options,
  });
}

/**
 * getShared skills
 *
 * @remarks
 * Fetch the publicly shared view of a skill by its share token. This endpoint is unauthenticated and only ever exposes the skill name, display name, summary, and latest content.
 */
export function useSharedSkillSuspense(
  request: GetSharedSkillRequest,
  options?: SuspenseQueryHookOptions<
    SharedSkillQueryData,
    SharedSkillQueryError
  >,
): UseSuspenseQueryResult<SharedSkillQueryData, SharedSkillQueryError> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildSharedSkillQuery(
      client,
      request,
      options,
    ),
    ...options,
  });
}

export function setSharedSkillData(
  client: QueryClient,
  queryKeyBase: [parameters: { token: string }],
  data: SharedSkillQueryData,
): SharedSkillQueryData | undefined {
  const key = queryKeySharedSkill(...queryKeyBase);

  return client.setQueryData<SharedSkillQueryData>(key, data);
}

export function invalidateSharedSkill(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<[parameters: { token: string }]>,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "skills", "getShared", ...queryKeyBase],
  });
}

export function invalidateAllSharedSkill(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "skills", "getShared"],
  });
}
